// Binder store using Svelte 5 runes
// Replaces state from public/state.js + logic from public/api.js handleLoadBinder()

import {
  CARDS_PER_PAGE_FACE,
  MD_BREAKPOINT,
  SELECTED_CARDS_CACHE_PREFIX,
} from '../logic/config.js';
import { sortCards } from '../logic/sort-logic.js';
import {
  getCardDisplayName,
  determineCurrencyFromLanguage,
  getBinderColor,
} from '../logic/utils.js';
import {
  extractSourceInfo,
  extractBinderSourceInfo,
  getCacheKeyFromSourceInfo,
} from '../logic/url-parser.js';
import { loadBinder } from '../services/binder-api.js';
import {
  processCard,
  initializeCollectionData,
  getCurrentSortArray,
} from '../services/collection-api.js';
import { setStorageItem, getStorageItem, removeStorageItem } from '../services/storage-manager.js';

// --- Reactive binder state ---
export const binderState = $state({
  // Core data
  allCardsData: [],
  selectedCards: [],
  currentSortMode: 'price',
  binderFullyLoaded: false,
  binderSyncStatus: 'complete', // 'complete' | 'syncing' | 'error'

  // Pagination
  currentLocation: 0,
  numPapers: 0,
  maxLocation: 0,

  // Collection state
  isCollection: false,
  collectionType: null,
  collectionSource: null,
  collectionSourceId: null,
  collectionData: null,
  colorOffsets: null, // Backend-provided color boundary offsets

  // Card cache (shared between sort arrays for collections)
  cardCache: new Map(),

  // UI
  binderName: '',
  binderOwner: '',
  binderColor: '#6b7280',
  selectedCurrency: 'usd',
  binderUrl: '', // the original URL or short path

  // Loading
  isLoading: false,
  loadingText: '',
  error: null, // { title, message } or null
});

// --- Derived state ---

/** Number of cards per page (9 for single face) */
export const cardsPerPage = CARDS_PER_PAGE_FACE;

/** Whether the viewport is mobile-sized */
export function isMobile() {
  return typeof window !== 'undefined' && window.innerWidth < MD_BREAKPOINT;
}

// --- Core functions ---

/**
 * Reset all binder state to defaults.
 * Called before loading a new binder.
 */
export function resetBinderState() {
  Object.assign(binderState, {
    allCardsData: [],
    selectedCards: [],
    currentSortMode: 'price',
    binderFullyLoaded: false,
    binderSyncStatus: 'complete',
    currentLocation: 0,
    numPapers: 0,
    maxLocation: 0,
    isCollection: false,
    collectionType: null,
    collectionSource: null,
    collectionSourceId: null,
    collectionData: null,
    colorOffsets: null,
    binderName: '',
    binderOwner: '',
    binderColor: '#6b7280',
    binderUrl: '',
    isLoading: false,
    loadingText: '',
    error: null,
  });
  binderState.cardCache.clear();
}

/**
 * Compute pagination values from the card count.
 * Desktop: page 0 = blank cover, pages 1..N show cards (front + back)
 * Mobile: all pages show cards (front only)
 */
export function computePagination(totalCards) {
  const mobile = isMobile();
  const cardsPerFace = CARDS_PER_PAGE_FACE;

  if (mobile) {
    // Mobile: each paper shows one face of 9 cards
    const numPapers = Math.max(1, Math.ceil(totalCards / cardsPerFace));
    binderState.numPapers = numPapers;
    binderState.maxLocation = numPapers - 1;
  } else {
    // Desktop: each paper shows front (9) + back (9) = 18 cards
    // Paper 0 = cover (blank front), paper 0 back = first 9 cards
    // Paper 1 front = next 9, paper 1 back = next 9, etc.
    const cardsPerPaper = cardsPerFace * 2;
    // Paper 0 front is a blank cover, so we need +1 paper to fit all cards.
    // Visible slots = (numPapers - 1) * 18, so numPapers = ceil(totalCards / 18) + 1.
    const numPapers = Math.max(1, Math.ceil(totalCards / cardsPerPaper) + 1);
    binderState.numPapers = numPapers;
    binderState.maxLocation = numPapers - 1;
  }
}

/**
 * Load and render a binder from a URL.
 * This is the main orchestrator — replaces handleLoadBinder() from api.js.
 *
 * @param {string} url - Full URL or short path (e.g., /mfb.xxx, https://moxfield.com/...)
 * @returns {Promise<Object|null>} Binder info object or null on error
 */
export async function loadAndRenderBinder(url) {
  // Reset state for new load
  resetBinderState();
  binderState.isLoading = true;
  binderState.loadingText = 'Loading binder...';
  binderState.binderUrl = url;
  binderState.binderColor = getBinderColor(url);
  binderState.selectedCurrency = determineCurrencyFromLanguage();

  // A cube is a Project's source, never a binder (CONTEXT.md "Source Link"):
  // refuse it before any request, so it can't open or land in the recents.
  if (extractSourceInfo(url) && !extractBinderSourceInfo(url)) {
    binderState.isLoading = false;
    binderState.error = {
      title: 'Not a binder',
      message: "A cube's cards are wanted, not offered, so it doesn't open as a binder.",
    };
    return null;
  }

  try {
    // A public page: no wait for auth (the star's want hits wait on their own).
    const result = await loadBinder(url, {
      currency: binderState.selectedCurrency,
      onSyncProgress: () => {
        binderState.loadingText = 'Syncing collection...';
      },
    });

    // Track sync status
    binderState.binderSyncStatus = result.syncStatus || 'complete';
    console.log(`[Binder Store] Sync status: ${binderState.binderSyncStatus}`);

    if (result.isCollection) {
      // Collection/binder: paginated with dual sort arrays
      binderState.isCollection = true;
      binderState.collectionType = result.collectionType || 'collection';
      binderState.collectionSource = result.source;
      binderState.collectionSourceId = result.sourceId;

      binderState.collectionData = initializeCollectionData({
        totalCards: result.totalCards,
        initialPriceCards: result.initialPriceCards,
        initialColorCards: result.initialColorCards,
        priceHasMore: result.priceHasMore,
        colorHasMore: result.colorHasMore,
        syncedAt: result.syncedAt,
        cardCache: binderState.cardCache,
        selectedCurrency: binderState.selectedCurrency,
        source: result.source,
        collectionType: result.collectionType,
        sourceId: result.sourceId,
      });

      // Store backend-provided color offsets
      binderState.colorOffsets = result.colorOffsets || null;

      // Default sort = price
      binderState.allCardsData = binderState.collectionData.byPrice;
      console.log(
        `[Binder Store] Collection initialized: ${binderState.allCardsData.length} price slots, ${binderState.collectionData.byColor.length} color slots`
      );
    } else {
      // Deck: all cards at once, sort client-side
      binderState.isCollection = false;
      binderState.collectionData = null;

      const processedCards = (result.initialPriceCards || result.cards || []).map((card) =>
        processCard(card, binderState.selectedCurrency)
      );

      // Sort by price initially
      binderState.allCardsData = sortCards(processedCards, 'price');
    }

    // Compute pagination
    computePagination(binderState.allCardsData.length);

    // Load cached selected cards
    loadSelectedCardsFromCache(url);

    // Set binder metadata
    const binderName = result.name || url.replace(/^(https?:\/\/)?(www\.)?/, '').replace(/\/$/, '');
    const binderOwner = result.owner || null;
    binderState.binderName = binderName;
    binderState.binderOwner = binderOwner;

    // Update loading state
    binderState.loadingText = 'Loading images...';
    binderState.binderFullyLoaded = true;
    binderState.isLoading = false;
    binderState.error = null;

    // Update document title
    document.title = binderOwner ? `${binderName} by ${binderOwner}` : binderName;

    return {
      name: binderName,
      owner: binderOwner,
      simpleUrl: result.simple_url,
      originalUrl: url,
    };
  } catch (error) {
    console.error('[Binder Store] Error loading binder:', error);
    binderState.isLoading = false;
    binderState.error = {
      title: 'Binder Not Found',
      message: "This binder doesn't exist or may have been deleted or made private.",
    };
    return null;
  }
}

/**
 * Sort the binder by mode ('price' or 'color').
 * For collections, swaps pre-sorted arrays.
 * For decks, sorts client-side.
 */
export function sortBinder(mode) {
  if (!['price', 'color'].includes(mode)) return;
  binderState.currentSortMode = mode;

  if (binderState.isCollection && binderState.collectionData) {
    // Collections: swap to the pre-sorted array
    binderState.allCardsData = getCurrentSortArray(
      true,
      mode,
      binderState.collectionData,
      binderState.allCardsData
    );
  } else {
    // Decks: sort client-side
    binderState.allCardsData = sortCards(binderState.allCardsData, mode);
  }

  // Reset to first page (page 1 on desktop to skip cover)
  computePagination(binderState.allCardsData.length);
  binderState.currentLocation = isMobile() ? 0 : Math.min(1, binderState.maxLocation);
}

/**
 * Navigate to the next page
 * @returns {boolean} True if page was turned
 */
export function goNextPage() {
  if (binderState.currentLocation >= binderState.maxLocation) return false;
  binderState.currentLocation++;
  return true;
}

/**
 * Navigate to the previous page
 * @returns {boolean} True if page was turned
 */
export function goPrevPage() {
  const minLocation = isMobile() ? 0 : 1;
  if (binderState.currentLocation <= minLocation) return false;
  binderState.currentLocation--;
  return true;
}

/**
 * Jump directly to a specific location (paper index), clamped to valid range.
 */
export function goToLocation(location) {
  const min = isMobile() ? 0 : 1;
  binderState.currentLocation = Math.max(min, Math.min(location, binderState.maxLocation));
}

/**
 * Get the collectionInfo object needed by collection-api functions
 */
export function getCollectionInfo() {
  return {
    source: binderState.collectionSource,
    sourceId: binderState.collectionSourceId,
    collectionType: binderState.collectionType,
    selectedCurrency: binderState.selectedCurrency,
  };
}

// --- Selection state ---

/**
 * Update the selection count for a card at a given index.
 * In normal mode, updates local state + persists to cache.
 * Trade mode selection will be handled in a future phase.
 */
export function updateSelectionState(originalIndex, newCount) {
  if (
    originalIndex === undefined ||
    originalIndex < 0 ||
    originalIndex >= binderState.allCardsData.length
  )
    return;

  const cardInfo = binderState.allCardsData[originalIndex];
  if (!cardInfo || cardInfo.status !== 'found' || !cardInfo.cardData) return;

  const cardId = cardInfo.cardData.id;
  const available = cardInfo.availableCount;
  newCount = Math.max(0, Math.min(newCount, available));

  if (cardInfo.selectedCount === newCount) return;

  cardInfo.selectedCount = newCount;

  // Update selectedCards array
  const selectedIndex = binderState.selectedCards.findIndex(
    (c) => c.id === cardId && (c.isFoil || false) === (cardInfo.isFoil || false)
  );

  if (newCount > 0) {
    const cardName = getCardDisplayName(cardInfo.cardData, cardInfo.originalLineRef);
    const selectedData = {
      id: cardId,
      name: cardName,
      set: cardInfo.cardData.set,
      set_name: cardInfo.cardData.set_name,
      set_symbol_url: cardInfo.cardData.set_symbol_url,
      collector_number: cardInfo.cardData.collector_number?.toLowerCase(),
      isFoil: cardInfo.isFoil,
      price: cardInfo.price,
      priceCurrency: cardInfo.priceCurrency,
      selectedCount: newCount,
      availableCount: available,
      lang: cardInfo.lang,
      rarity: cardInfo.cardData.rarity,
    };
    if (selectedIndex > -1) {
      binderState.selectedCards[selectedIndex] = selectedData;
    } else {
      binderState.selectedCards.push(selectedData);
    }
  } else {
    if (selectedIndex > -1) {
      binderState.selectedCards.splice(selectedIndex, 1);
    }
  }

  // Persist to cache
  saveSelectedCardsToCache();
}

// --- Selection cache helpers ---

function getSelectionCacheKey(url) {
  const binderUrl = url || binderState.binderUrl;
  const sourceInfo = extractSourceInfo(binderUrl);
  return getCacheKeyFromSourceInfo(sourceInfo, SELECTED_CARDS_CACHE_PREFIX);
}

function saveSelectedCardsToCache() {
  const cacheKey = getSelectionCacheKey();
  if (!cacheKey) return;

  if (binderState.selectedCards.length > 0) {
    const cacheableData = binderState.selectedCards.map((card) => ({
      scryfallId: card.id,
      isFoil: card.isFoil || false,
      selectedCount: card.selectedCount,
    }));
    setStorageItem(cacheKey, cacheableData, { expiresInDays: 30 });
  } else {
    removeStorageItem(cacheKey);
  }
}

function loadSelectedCardsFromCache(url) {
  const cacheKey = getSelectionCacheKey(url);
  if (!cacheKey) return;

  const cachedSelections = getStorageItem(cacheKey);
  if (!cachedSelections || !Array.isArray(cachedSelections)) return;

  try {
    const cachedSelectionsMap = new Map(
      cachedSelections.map((item) => {
        const compositeKey = `${item.scryfallId}_${item.isFoil || false}`;
        return [compositeKey, item.selectedCount];
      })
    );

    binderState.allCardsData.forEach((cardInfo) => {
      if (cardInfo.cardData?.id) {
        const compositeKey = `${cardInfo.cardData.id}_${cardInfo.isFoil || false}`;
        if (cachedSelectionsMap.has(compositeKey)) {
          const cachedCount = cachedSelectionsMap.get(compositeKey);
          cardInfo.selectedCount = Math.max(0, Math.min(cachedCount, cardInfo.availableCount));
        }
      }
    });

    rebuildSelectedCardsArray();
  } catch (error) {
    console.error('[Binder Store] Error loading selected cards from cache:', error);
    removeStorageItem(cacheKey);
  }
}

function rebuildSelectedCardsArray() {
  binderState.selectedCards = [];
  binderState.allCardsData.forEach((cardInfo) => {
    if (cardInfo.selectedCount > 0 && cardInfo.cardData && cardInfo.status === 'found') {
      const cardName = getCardDisplayName(cardInfo.cardData, cardInfo.originalLineRef);
      binderState.selectedCards.push({
        id: cardInfo.cardData.id,
        name: cardName,
        set: cardInfo.cardData.set,
        set_name: cardInfo.cardData.set_name,
        set_symbol_url: cardInfo.cardData.set_symbol_url,
        collector_number: cardInfo.cardData.collector_number?.toLowerCase(),
        isFoil: cardInfo.isFoil,
        price: cardInfo.price,
        priceCurrency: cardInfo.priceCurrency,
        selectedCount: cardInfo.selectedCount,
        availableCount: cardInfo.availableCount,
        lang: cardInfo.lang,
        rarity: cardInfo.cardData.rarity,
      });
    }
  });
}
